/**
 * @file bbd-datalayer-ecommerce.js
 * @description GA4 ecommerce tracking via dataLayer. Listens to theme events
 * and page-load data to push GA4 standard ecommerce events.
 *
 * Integration points:
 * - Reads data-datalayer-item JSON attributes from product cards (view_item_list, select_item)
 * - Reads <script id="bbd-datalayer-product"> JSON block on PDP (view_item)
 * - Listens to cart:update theme event (add_to_cart, remove_from_cart, view_cart)
 * - Listens to variant:update theme event (view_item on variant change)
 * - Reads data-datalayer-promotion-* attributes from bbd-promotion-banner (view_promotion, select_promotion)
 */

import { ThemeEvents } from '@theme/events';
import {
  startRebuyAttribution,
  reportRebuyViewed,
  consumeRebuyAttribution,
  reportRebuyAdd,
  reportRebuyRemove
} from '@bbd/rebuy-attribution';

const BRAND = 'Pig & Hen';

// ---------------------------------------------------------------------------
// Cross-page list attribution — persists the list a product was clicked from
// so the PDP's view_item/add_to_cart can report the same item_list_name,
// even though it's a full page navigation away from the list.
// ---------------------------------------------------------------------------
const LIST_ATTRIBUTION_KEY = 'bbd_list_attribution';
const LIST_ATTRIBUTION_TTL_MS = 10 * 60 * 1000;

const setListAttribution = (productId, listName) => {
  if (!productId || !listName) return;
  try {
    sessionStorage.setItem(
      LIST_ATTRIBUTION_KEY,
      JSON.stringify({ productId: String(productId), listName, expiresAt: Date.now() + LIST_ATTRIBUTION_TTL_MS })
    );
  } catch {
    // sessionStorage unavailable (private mode, etc.) — silently skip attribution
  }
};

const readListAttribution = (productId) => {
  try {
    const raw = sessionStorage.getItem(LIST_ATTRIBUTION_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || String(entry.productId) !== String(productId) || Date.now() > entry.expiresAt) return null;
    return entry;
  } catch {
    return null;
  }
};

/** Read the attributed list name without consuming it (used for view_item). */
const peekListAttribution = (productId) => readListAttribution(productId)?.listName ?? '';

/** Read and clear the attributed list name (used once add_to_cart actually happens). */
const consumeListAttribution = (productId) => {
  const entry = readListAttribution(productId);
  if (!entry) return '';
  try {
    sessionStorage.removeItem(LIST_ATTRIBUTION_KEY);
  } catch {
    // ignore
  }
  return entry.listName;
};

/** Push to dataLayer, clearing previous ecommerce object first. */
const push = (eventObj) => {
  window.dataLayer = window.dataLayer || [];
  if (eventObj.ecommerce) {
    window.dataLayer.push({ ecommerce: null });
    eventObj = { ...eventObj, ecommerce: { taxes_included: true, ...eventObj.ecommerce } };
  }
  window.dataLayer.push(eventObj);
};

/**
 * Map a Shopify product + variant object to a GA4 items array item.
 * @param {object} product - Shopify product object (from JSON)
 * @param {object} variant - Shopify variant object
 * @param {number} [index=0]
 * @param {string} [listName='']
 */
const mapProductVariant = (product, variant, index = 0, listName = '', productMeta = {}) => {
  const tags = Array.isArray(product.tags)
    ? product.tags
    : String(product.tags || '')
        .split(',')
        .map((t) => t.trim());

  let gender = 'Unisex';
  if (tags.some((t) => t.includes('Men'))) gender = 'Male';
  else if (tags.some((t) => t.includes('Women'))) gender = 'Female';

  return {
    item_id: String(variant.id),
    item_group_id: String(product.id),
    item_name: productMeta.product_information_name || product.title,
    item_brand: BRAND,
    item_category: productMeta.category_name || '',
    item_category2: productMeta.anchor_collection_title || '',
    item_category3: gender,
    item_variant: productMeta.product_variant_name || '',
    item_size: variant.title || '',
    item_list_name: listName,
    index,
    price: (variant.price || 0) / 100,
    quantity: 1
  };
};

/**
 * Map a Shopify cart line item to a GA4 items array item.
 * Cart line items already have option1/2/3 and product_type.
 * @param {object} lineItem - Cart API line item
 * @param {number} [index=0]
 * @param {string} [listName='']
 * @param {object} [productMeta={}] - Optional metafield overrides (product_variant_name, product_information_name)
 */
const mapCartLineItem = (lineItem, index = 0, listName = '', productMeta = {}) => {
  const gender = productMeta.gender || 'Unisex';

  return {
    item_id: String(lineItem.variant_id),
    item_group_id: String(lineItem.product_id),
    item_name: productMeta.product_information_name || lineItem.product_title || lineItem.title,
    item_brand: BRAND,
    item_category: productMeta.category_name || '',
    item_category2: productMeta.anchor_collection_title || '',
    item_category3: gender,
    item_variant: productMeta.product_variant_name || '',
    item_size: lineItem.variant_title || '',
    item_list_name: listName,
    index,
    price: (lineItem.price || 0) / 100,
    quantity: lineItem.quantity || 1
  };
};

/**
 * Parse a prebuilt GA4 item object from a data attribute.
 * Used for view_item_list / select_item from product cards.
 * @param {HTMLElement} el - Element with data-datalayer-item attribute
 * @returns {object|null}
 */
const parseDatalayerItem = (el) => {
  try {
    return JSON.parse(el.dataset.datalayerItem);
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// view_item_list — one event per product list container
// ---------------------------------------------------------------------------
const fireViewItemListForContainer = (container) => {
  const listName = container.dataset.datalayerListName ?? '';
  let items = [];

  const cards = container.querySelectorAll('[data-datalayer-item]');
  if (cards.length) {
    // Standard pattern: individual data-datalayer-item attributes on each product card
    items = Array.from(cards)
      .map((card, idx) => {
        const item = parseDatalayerItem(card);
        if (!item) return null;
        return { ...item, index: idx, item_list_name: item.item_list_name || listName };
      })
      .filter(Boolean);
  } else if (container.dataset.datalayerItems) {
    // Slideshow pattern: items embedded as a JSON array on the container itself
    // (used when individual slide attributes cannot survive HTML escaping)
    try {
      items = JSON.parse(container.dataset.datalayerItems);
    } catch {
      return;
    }
  }

  if (!items.length) return;

  push({
    event: 'view_item_list',
    ecommerce: {
      currency: window.Shopify?.currency?.active ?? 'EUR',
      item_list_name: listName,
      items
    }
  });
};

const initViewItemList = () => {
  // Static sections: fire when the container enters the viewport
  const observer = new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        obs.unobserve(entry.target);
        fireViewItemListForContainer(entry.target);
      }
    },
    { rootMargin: '0px' }
  );

  document.querySelectorAll('[data-datalayer-list-name]').forEach((el) => {
    // Only observe containers that already have items rendered server-side.
    // Dynamic sections dispatch datalayer:list-ready after their JS render.
    // Deferred containers (cart drawer sections) are reported when their section opens, see bbd-cart-sections.js.
    if (el.querySelector('[data-datalayer-item]') && !el.hasAttribute('data-datalayer-deferred')) {
      observer.observe(el);
    }
  });

  // Dynamic sections: fire after JS has injected product cards into the DOM
  document.addEventListener('datalayer:list-ready', (e) => {
    const container = e.detail?.container;
    if (container) fireViewItemListForContainer(container);
  });
};

// ---------------------------------------------------------------------------
// select_item — when a product card is clicked
// ---------------------------------------------------------------------------
const initSelectItem = () => {
  document.addEventListener('click', (e) => {
    const card = e.target.closest('[data-datalayer-item]');
    if (!card) return;

    const item = parseDatalayerItem(card);
    if (!item) return;

    const listName = card.closest('[data-datalayer-list-name]')?.dataset.datalayerListName ?? item.item_list_name ?? '';

    push({
      event: 'select_item',
      ecommerce: {
        currency: window.Shopify?.currency?.active ?? 'EUR',
        item_list_name: listName,
        items: [{ ...item, item_list_name: listName }]
      }
    });

    setListAttribution(card.dataset.datalayerProductId, listName);
    startRebuyAttribution(card.dataset.datalayerProductId, listName);
  });
};

// ---------------------------------------------------------------------------
// Rebuy "viewed" — also counts as a product view when its quick-add modal is
// opened (the "Choose options" button), not just a full PDP navigation.
// ---------------------------------------------------------------------------
const initQuickAddViewed = () => {
  document.addEventListener('click', (e) => {
    const chooseButton = e.target.closest('.quick-add__button--choose');
    if (!chooseButton) return;

    const productId = chooseButton.closest('product-form-component')?.dataset.productId;
    if (!productId) return;

    const listName = chooseButton.closest('[data-datalayer-list-name]')?.dataset.datalayerListName ?? '';
    reportRebuyViewed(productId, listName);
  });
};

// ---------------------------------------------------------------------------
// view_item — on PDP load and variant change
// ---------------------------------------------------------------------------

// bbd-variant-picker auto-selects the "most popular" size right after load,
// which dispatches variant:update. Without this, the PDP pushed view_item for
// the server-rendered variant and again for the auto-selected one. While the
// auto-select is in flight, the load view_item is held back and the
// variant:update handler pushes the only one. The timer is a fallback for
// when that update never arrives (failed fetch, no matching variant).
const AUTO_SELECT_VIEW_ITEM_TIMEOUT_MS = 3000;
let pendingViewItemTimer = null;

const isPdpPickerAutoSelecting = (productId) =>
  Array.from(document.querySelectorAll(`bbd-variant-picker[data-product-id="${productId}"]`)).some(
    (picker) => !picker.closest('quick-add-dialog') && picker.isAutoSelecting
  );

const pushViewItem = (product, variant, listName = '', productMeta = {}) => {
  const item = mapProductVariant(product, variant, 0, listName, productMeta);
  push({
    event: 'view_item',
    ecommerce: {
      currency: window.Shopify?.currency?.active ?? 'EUR',
      value: item.price,
      items: [item]
    }
  });
};

const initViewItem = () => {
  const scriptEl = document.getElementById('bbd-datalayer-product');
  if (!scriptEl) return;

  try {
    const {
      product,
      variant,
      list_name: listName,
      product_variant_name,
      product_information_name,
      anchor_collection_title,
      category_name
    } = JSON.parse(scriptEl.textContent);
    const attributedListName = listName || peekListAttribution(product.id);
    const pushInitialViewItem = () => {
      pendingViewItemTimer = null;
      pushViewItem(product, variant, attributedListName, {
        product_variant_name,
        product_information_name,
        anchor_collection_title,
        category_name
      });
    };

    if (isPdpPickerAutoSelecting(product.id)) {
      pendingViewItemTimer = setTimeout(pushInitialViewItem, AUTO_SELECT_VIEW_ITEM_TIMEOUT_MS);
    } else {
      pushInitialViewItem();
    }

    // Rebuy "viewed" means the PDP of a recommended product was actually
    // visited (like view_item), not that it appeared in a carousel — only
    // report it here, once, on PDP load. Not repeated on variant change,
    // since that's still the same product. Uses the separate 24h Rebuy
    // attribution store, not GA4's 10-minute one used for attributedListName.
    reportRebuyViewed(product.id, listName);
  } catch {
    // not a PDP
  }
};

// ---------------------------------------------------------------------------
// view_item on variant change — listen to variant:update theme event
// ---------------------------------------------------------------------------
const initVariantUpdate = () => {
  const scriptEl = document.getElementById('bbd-datalayer-product');
  if (!scriptEl) return;

  let productData;
  try {
    productData = JSON.parse(scriptEl.textContent);
  } catch {
    return;
  }

  document.addEventListener(ThemeEvents.variantUpdate, (e) => {
    const newVariant = e.detail?.resource;
    if (!newVariant) return;

    // variant:update bubbles from any variant picker on the page (e.g. a swatch
    // hover-preview on an unrelated product card), not just this PDP's own picker.
    // Ignore updates that aren't for the product we're tracking.
    const updateProductId = e.detail?.data?.productId;
    if (updateProductId && String(updateProductId) !== String(productData.product?.id)) return;

    // This update replaces the held-back load view_item (see isPdpPickerAutoSelecting)
    clearTimeout(pendingViewItemTimer);
    pendingViewItemTimer = null;

    // Merge updated variant into known product data
    const updatedVariant = {
      ...newVariant,
      price: newVariant.price ?? productData.variant?.price
    };

    const attributedListName = productData.list_name || peekListAttribution(productData.product?.id);

    pushViewItem(productData.product, updatedVariant, attributedListName, {
      product_variant_name: productData.product_variant_name,
      product_information_name: productData.product_information_name,
      anchor_collection_title: productData.anchor_collection_title,
      category_name: productData.category_name
    });
  });
};

// ---------------------------------------------------------------------------
// Product meta cache — keyed by product_id (number)
// Populated from [data-datalayer-product] elements and #bbd-datalayer-cart-meta.
// Gives mapCartLineItem access to metafields the cart API doesn't return.
// ---------------------------------------------------------------------------
const productMetaCache = new Map();

const buildProductMetaCache = () => {
  // Read from all scoped data-datalayer-product JSON blocks (buy buttons on page)
  document.querySelectorAll('[data-datalayer-product]').forEach((el) => {
    try {
      const {
        product,
        product_variant_name,
        product_information_name,
        anchor_collection_title,
        category_name,
        gender
      } = JSON.parse(el.textContent);
      if (product?.id) {
        productMetaCache.set(product.id, {
          product_variant_name,
          product_information_name,
          anchor_collection_title,
          category_name,
          gender
        });
      }
    } catch {
      // ignore malformed JSON
    }
  });

  // Read from cart-page meta script (rendered via main-cart.liquid)
  const cartMetaEl = document.getElementById('bbd-datalayer-cart-meta');
  if (cartMetaEl) {
    try {
      const cartMeta = JSON.parse(cartMetaEl.textContent);
      for (const [productId, meta] of Object.entries(cartMeta)) {
        productMetaCache.set(parseInt(productId, 10), meta);
      }
    } catch {
      // ignore malformed JSON
    }
  }
};

const getProductMeta = (productId) => productMetaCache.get(productId) ?? {};

// ---------------------------------------------------------------------------
// Snapshot of previous cart state for add/remove detection
// ---------------------------------------------------------------------------
const state = {
  previousCartItems: null,
  lastViewCartDrawerOpenAt: 0
};

const getCartItemMap = (items) => Object.fromEntries(items.map((item) => [item.key, item.quantity]));

/**
 * Handle add_to_cart for product-form-component adds (CartAddEvent with empty resource).
 * Reads product/variant data directly from the PDP's #bbd-datalayer-product JSON block.
 */
const handleProductFormAdd = async (e) => {
  const variantId = parseInt(e.target?.querySelector('[name="id"]')?.value, 10);
  if (!variantId) return false;

  // Read product data scoped to the form that fired the event — works on PDP,
  // quick-add-modal, and any other context without global ID collisions.
  const productScriptEl = e.target?.querySelector('[data-datalayer-product]');

  if (productScriptEl) {
    let productData;
    try {
      productData = JSON.parse(productScriptEl.textContent);
    } catch {
      return false;
    }

    const {
      product,
      list_name: listName = '',
      product_variant_name,
      product_information_name,
      anchor_collection_title,
      category_name
    } = productData;
    const variant = product.variants?.find((v) => v.id === variantId);
    if (!variant) return false;

    // Product cards inside a tracked list already carry data-datalayer-list-name
    // on an ancestor (same attribute select_item reads) — prefer that over the
    // JSON's own list_name (which is only ever non-empty on the PDP today),
    // then fall back to a persisted cross-page click attribution.
    const domListName = e.target?.closest('[data-datalayer-list-name]')?.dataset.datalayerListName ?? '';
    const attributedListName = domListName || listName || consumeListAttribution(product.id);
    const rebuyListName = domListName || listName || consumeRebuyAttribution(product.id);
    const quantity = parseInt(e.target?.querySelector('[name="quantity"]')?.value ?? '1', 10) || 1;
    const item = {
      ...mapProductVariant(product, variant, 0, attributedListName, {
        product_variant_name,
        product_information_name,
        anchor_collection_title,
        category_name
      }),
      quantity
    };
    const currency = window.Shopify?.currency?.active ?? 'EUR';

    push({
      event: 'add_to_cart',
      ecommerce: { currency, value: item.price * quantity, items: [item] }
    });

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      state.previousCartItems = cart.items;
      const cartLineItem = cart.items.find((lineItem) => lineItem.variant_id === variantId);
      reportRebuyAdd(cartLineItem, rebuyListName);
    } catch {
      // silently fail
    }

    return true;
  }

  // Fallback: no data-datalayer-product found (safety net for any product-form-component
  // not rendered via buy-buttons.liquid). Fetch cart and look up by variant ID.
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    state.previousCartItems = cart.items;

    const lineItem = cart.items.find((item) => item.variant_id === variantId);
    if (!lineItem) return false;

    const quantity = parseInt(e.target?.querySelector('[name="quantity"]')?.value ?? '1', 10) || 1;
    const item = { ...mapCartLineItem({ ...lineItem, quantity }, 0) };
    const currency = cart.currency || window.Shopify?.currency?.active || 'EUR';

    push({
      event: 'add_to_cart',
      ecommerce: { currency, value: item.price * quantity, items: [item] }
    });
  } catch {
    // silently fail
  }

  return true;
};

// ---------------------------------------------------------------------------
// add_to_cart / remove_from_cart — listen to cart:update theme event
// ---------------------------------------------------------------------------
const CART_SOURCE_LIST_NAMES = { 'bbd-giftbox-picker': 'Cart-Drawer Giftboxes' };

const initCartEvents = () => {
  document.addEventListener(ThemeEvents.cartUpdate, async (e) => {
    // CartAddEvent from product-form-component dispatches with empty resource ({}).
    // Handle these separately using PDP product JSON to get full item data.
    if (e.detail?.data?.source === 'product-form-component') {
      await handleProductFormAdd(e);
      return;
    }

    // CartUpdateEvent from bbd-variant-picker dispatches with the /cart/add.js
    // single line item as resource (no .items array) and variantId in data.
    if (e.detail?.data?.variantId && !e.detail?.resource?.items) {
      const lineItem = e.detail.resource;
      if (lineItem?.variant_id) {
        // Prefer metafields passed directly from the picker's JSON block;
        // fall back to the page-level meta cache (other buy-button contexts).
        const metaFromEvent = {
          product_variant_name: e.detail.data.product_variant_name,
          product_information_name: e.detail.data.product_information_name,
          anchor_collection_title: e.detail.data.anchor_collection_title,
          category_name: e.detail.data.category_name,
          gender: e.detail.data.gender
        };
        const hasMeta =
          metaFromEvent.product_variant_name !== undefined || metaFromEvent.product_information_name !== undefined;
        const meta = hasMeta ? metaFromEvent : getProductMeta(lineItem.product_id);

        // Same list-attribution logic as handleProductFormAdd: prefer an
        // ancestor's data-datalayer-list-name (e.g. bbd-variant-picker used
        // inside a quick-add modal on a list page), otherwise fall back to a
        // persisted cross-page click attribution.
        const domListName = e.target?.closest('[data-datalayer-list-name]')?.dataset.datalayerListName ?? '';
        const attributedListName = domListName || consumeListAttribution(lineItem.product_id);
        const rebuyListName = domListName || consumeRebuyAttribution(lineItem.product_id);

        const item = mapCartLineItem(lineItem, 0, attributedListName, meta);
        const currency = window.Shopify?.currency?.active ?? 'EUR';
        push({
          event: 'add_to_cart',
          ecommerce: {
            currency,
            value: item.price * item.quantity,
            items: [item]
          }
        });
        reportRebuyAdd(lineItem, rebuyListName);

        try {
          const res = await fetch('/cart.js');
          const freshCart = await res.json();
          state.previousCartItems = freshCart.items;
        } catch {
          // silently fail
        }
      }
      return;
    }

    const cart = e.detail?.resource;
    if (!cart || !cart.items) return;

    const previousMap = state.previousCartItems ? getCartItemMap(state.previousCartItems) : {};
    const currentMap = getCartItemMap(cart.items);

    // Determine added / removed items by comparing old vs new state
    const addedItems = [];
    const removedItems = [];
    const addedLineItems = [];
    const removedProductIds = [];

    // Cart updates of the cart drawer sections that have a list of their own
    const sourceListName = CART_SOURCE_LIST_NAMES[e.detail?.data?.source] ?? '';

    for (const [idx, item] of cart.items.entries()) {
      const prevQty = previousMap[item.key] ?? 0;
      const diff = item.quantity - prevQty;
      if (diff > 0) {
        addedItems.push({
          ...mapCartLineItem({ ...item, quantity: diff }, idx, sourceListName, getProductMeta(item.product_id)),
          quantity: diff
        });
        addedLineItems.push(item);
      }
    }

    if (state.previousCartItems) {
      for (const [idx, item] of state.previousCartItems.entries()) {
        const currQty = currentMap[item.key] ?? 0;
        const diff = item.quantity - currQty;
        if (diff > 0) {
          removedItems.push({
            ...mapCartLineItem({ ...item, quantity: diff }, idx, '', getProductMeta(item.product_id)),
            quantity: diff
          });
          removedProductIds.push(item.product_id);
        }
      }
    }

    const currency = cart.currency || window.Shopify?.currency?.active || 'EUR';

    if (addedItems.length) {
      const value = addedItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      push({
        event: 'add_to_cart',
        ecommerce: {
          currency,
          value,
          items: addedItems
        }
      });
      for (const lineItem of addedLineItems) {
        reportRebuyAdd(lineItem, consumeRebuyAttribution(lineItem.product_id));
      }
    }

    if (removedItems.length) {
      const value = removedItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      push({
        event: 'remove_from_cart',
        ecommerce: {
          currency,
          value,
          items: removedItems
        }
      });
      for (const productId of removedProductIds) {
        reportRebuyRemove(productId);
      }
    }

    // Update snapshot
    state.previousCartItems = cart.items;
  });
};

// ---------------------------------------------------------------------------
// view_cart — on cart page load and when cart drawer opens
// ---------------------------------------------------------------------------
const pushViewCart = (cartData) => {
  if (!cartData?.items?.length) return;
  const currency = cartData.currency || window.Shopify?.currency?.active || 'EUR';
  const items = cartData.items.map((item, idx) => mapCartLineItem(item, idx, '', getProductMeta(item.product_id)));
  const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  push({
    event: 'view_cart',
    ecommerce: { currency, value, items }
  });

  // Seed the previous cart state snapshot
  state.previousCartItems = cartData.items;
};

const initViewCart = () => {
  const scriptEl = document.getElementById('bbd-datalayer-cart');
  if (!scriptEl) return;

  try {
    const cartData = JSON.parse(scriptEl.textContent);
    pushViewCart(cartData);
  } catch {
    // not on cart page
  }
};

// ---------------------------------------------------------------------------
// Cart drawer open → view_cart
// ---------------------------------------------------------------------------
const initCartDrawerViewCart = () => {
  const handleCartDrawerOpen = async (event) => {
    const source = event.target;
    if (!(source instanceof HTMLElement)) return;
    if (source.tagName.toLowerCase() !== 'cart-drawer-component') return;

    const now = Date.now();
    if (state.lastViewCartDrawerOpenAt && now - state.lastViewCartDrawerOpenAt < 800) return;
    state.lastViewCartDrawerOpenAt = now;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      pushViewCart(cart);
    } catch {
      // silently fail
    }
  };

  // `dialog:open` is dispatched from the component itself and does not bubble to document.
  // Bind directly to cart drawer components.
  document.querySelectorAll('cart-drawer-component').forEach((drawer) => {
    drawer.addEventListener('dialog:open', handleCartDrawerOpen);
  });
};

// ---------------------------------------------------------------------------
// begin_checkout — listen to checkout button click
// ---------------------------------------------------------------------------
const initBeginCheckout = () => {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[name="checkout"], [href="/checkout"]');
    if (!btn) return;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      if (!cart?.items?.length) return;

      const currency = cart.currency || window.Shopify?.currency?.active || 'EUR';
      const items = cart.items.map((item, idx) => mapCartLineItem(item, idx, '', getProductMeta(item.product_id)));
      const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const coupon = cart.cart_level_discount_applications?.[0]?.title ?? '';

      push({
        event: 'begin_checkout',
        ecommerce: {
          currency,
          value,
          coupon,
          items
        }
      });
    } catch {
      // silently fail
    }
  });
};

// ---------------------------------------------------------------------------
// Public API — exposed on window.BBDTracking for Liquid inline calls
// ---------------------------------------------------------------------------
window.BBDTracking = {
  push,
  mapProductVariant,
  mapCartLineItem,
  pushViewItem,
  pushViewCart
};

// ---------------------------------------------------------------------------
// Cart snapshot seed — fetch cart on page load so the diff logic always has
// a valid baseline, even when the cart drawer has never been opened.
// ---------------------------------------------------------------------------
const initCartSnapshot = async () => {
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    // Only seed if not already set by initViewCart (cart page)
    if (state.previousCartItems === null) {
      state.previousCartItems = cart.items;
    }
  } catch {
    // silently fail
  }
};

// ---------------------------------------------------------------------------
// view_promotion / select_promotion — bbd-promotion-banner (header group)
// view_promotion fires once per page view, when the banner enters the viewport.
// Dismissed or expired banners are display: none and never intersect.
// ---------------------------------------------------------------------------
/** @param {HTMLElement} banner */
const getPromotion = (banner) => ({
  promotion_id: banner.dataset.datalayerPromotionId ?? '',
  promotion_name: banner.dataset.datalayerPromotionName ?? '',
  creative_slot: 'header'
});

const initViewPromotion = () => {
  const observer = new IntersectionObserver((entries, obs) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      obs.unobserve(entry.target);
      push({ event: 'view_promotion', ecommerce: getPromotion(entry.target) });
    }
  });

  document.querySelectorAll('.bbd-promotion-banner').forEach((banner) => observer.observe(banner));
};

const initSelectPromotion = () => {
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.bbd-promotion-banner__link');
    if (!link) return;

    const banner = link.closest('.bbd-promotion-banner');
    if (!banner) return;

    push({ event: 'select_promotion', ecommerce: getPromotion(banner) });
  });
};

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  buildProductMetaCache();
  initViewItemList();
  initSelectItem();
  initQuickAddViewed();
  initViewItem();
  initVariantUpdate();
  initViewCart();
  initCartEvents();
  initBeginCheckout();
  initCartDrawerViewCart();
  initCartSnapshot();
  initViewPromotion();
  initSelectPromotion();
});
